/**
 * DocTooltip — Rich tooltip for document items in the sidebar.
 *
 * Shows metadata (company, type, period, pages) and conceptual summary
 * on hover with 300ms delay. Uses React Portal to render outside
 * overflow:hidden containers (virtual scroll, max-h sections).
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { formatPeriod } from '../../utils/formatPeriod';
import { useTheme } from '../../contexts/ThemeContext';

/* ─── Hook ────────────────────────────────────────────────────────── */

interface TooltipState {
  doc: DocTooltipDoc;
  rect: DOMRect;
}

export function useDocTooltip(delay = 300) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const timerRef = useRef<number>(0);

  const onMouseEnter = useCallback(
    (doc: DocTooltipDoc, e: React.MouseEvent) => {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        setTooltip({ doc, rect });
      }, delay);
    },
    [delay]
  );

  const onMouseLeave = useCallback(() => {
    clearTimeout(timerRef.current);
    setTooltip(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => clearTimeout(timerRef.current), []);

  return { tooltip, onMouseEnter, onMouseLeave };
}

/* ─── Component ───────────────────────────────────────────────────── */

/** Minimal fields needed for tooltip display. Covers both Document and RecentDoc. */
export interface DocTooltipDoc {
  name?: string;
  original_filename?: string;
  company_name?: string;
  document_type?: string;
  document_period?: string;
  page_count?: number;
  conceptual_summary?: string;
}

interface DocTooltipProps {
  doc: DocTooltipDoc;
  anchor: DOMRect;
}

const TOOLTIP_GAP = 8;

const DocTooltip: React.FC<DocTooltipProps> = ({ doc, anchor }) => {
  const { theme } = useTheme();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!tooltipRef.current) return;
    const el = tooltipRef.current;
    const { innerWidth, innerHeight } = window;
    const elRect = el.getBoundingClientRect();

    let left = anchor.right + TOOLTIP_GAP;
    let top = anchor.top;

    // If tooltip overflows right, place it to the left of the anchor
    if (left + elRect.width > innerWidth - TOOLTIP_GAP) {
      left = anchor.left - elRect.width - TOOLTIP_GAP;
    }

    // If tooltip overflows bottom, shift it up
    if (top + elRect.height > innerHeight - TOOLTIP_GAP) {
      top = innerHeight - elRect.height - TOOLTIP_GAP;
    }

    // Clamp top to not go above viewport
    if (top < TOOLTIP_GAP) top = TOOLTIP_GAP;

    setPos({ top, left });
  }, [anchor]);

  const hasSummary = !!doc.conceptual_summary;

  // Build separate metadata parts for richer display
  const companyName = doc.company_name;
  const metaParts: string[] = [];
  if (doc.document_type) metaParts.push(doc.document_type);
  if (doc.document_period) metaParts.push(formatPeriod(doc.document_period, doc.document_type));
  if (doc.page_count) metaParts.push(`${doc.page_count} pág.`);
  const metaLine = metaParts.join(' \u2022 ');

  // For private docs without company_name, use the formatted metadata string
  const displayName = companyName || doc.name || doc.original_filename;

  return ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      className={`fixed z-[60] max-w-[280px] rounded-lg shadow-lg border p-3 pointer-events-none transition-opacity duration-150 ${
        pos ? 'opacity-100' : 'opacity-0'
      } ${theme.colors.bg.secondary} ${theme.colors.border.primary}`}
      style={{
        top: pos?.top ?? anchor.top,
        left: pos?.left ?? anchor.right + TOOLTIP_GAP,
      }}
    >
      <div className={`text-xs font-semibold ${theme.colors.text.primary} truncate`}>
        {displayName}
      </div>
      {metaLine && (
        <div className={`text-[10px] mt-0.5 ${theme.colors.text.secondary}`}>{metaLine}</div>
      )}
      {hasSummary && (
        <>
          <div className={`my-1.5 border-t ${theme.colors.border.primary}`} />
          <div className={`text-xs leading-snug ${theme.colors.text.secondary} line-clamp-3`}>
            {doc.conceptual_summary}
          </div>
        </>
      )}
    </div>,
    document.body
  );
};

export default DocTooltip;
